import {
  ApiError,
  type Article,
  type ArticleAction,
  type ArticleImage,
  type ArticleRequest,
  type ArticleSummary,
  type Member,
  type MyOrganization,
  type Practice,
  type Session,
  type SocialPlatform,
  type SocialPostPage,
  type SocialPostShow,
  type SocialPostSort,
  type User,
} from "@brandex/api-client";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useBrandex } from "./context";

/** Query keys in one place so invalidation stays consistent. */
export const keys = {
  me: ["me"] as const,
  subscription: (orgId: string) => ["orgs", orgId, "subscription"] as const,
  changeRequests: (orgId: string) => ["orgs", orgId, "change-requests"] as const,
  publicPlans: ["public", "plans"] as const,
  orgs: ["me", "organizations"] as const,
  sessions: ["auth", "sessions"] as const,
  members: (orgId: string) => ["orgs", orgId, "members"] as const,
  practice: (orgId: string) => ["orgs", orgId, "practice"] as const,
  /** A pending authenticator enrollment; kept until confirmed or abandoned. */
  mfaEnrollment: ["auth", "mfa", "enrollment"] as const,
  faq: (orgId: string) => ["orgs", orgId, "faq"] as const,
  articles: (orgId: string) => ["orgs", orgId, "articles"] as const,
  article: (orgId: string, id: string) => ["orgs", orgId, "articles", id] as const,
  /** Every social panel and post list of a practice (ADR 0024). */
  social: (orgId: string) => ["orgs", orgId, "social"] as const,
  socialPosts: (orgId: string, platform: string, q: SocialPostFilter) => ["orgs", orgId, "social", platform, "posts", q] as const,
};

/** How a social post list is filtered (ADR 0024). */
export interface SocialPostFilter {
  sort: SocialPostSort;
  show: SocialPostShow;
  q: string;
}

function required<T>(data: T | undefined): T {
  if (data === undefined) throw new Error("Empty response body");
  return data;
}

export function useMe(enabled = true) {
  const { api } = useBrandex();
  return useQuery<User>({
    queryKey: keys.me,
    enabled,
    queryFn: async () => required((await api.http.GET("/v1/me")).data),
  });
}

export function useMyOrganizations(enabled = true) {
  const { api } = useBrandex();
  return useQuery<MyOrganization[]>({
    queryKey: keys.orgs,
    enabled,
    queryFn: async () => required((await api.http.GET("/v1/me/organizations")).data),
  });
}

/**
 * Whether the signed-in account is BrandEx staff (ADR 0022). Asked only when
 * it has no practice, so doctors never pay for the call.
 */
export function useStaffAccount(enabled: boolean) {
  const { api } = useBrandex();
  return useQuery<boolean>({
    queryKey: ["me", "staff-access", "is-staff"],
    enabled,
    queryFn: async () => required((await api.http.GET("/v1/me/staff-access")).data).is_staff,
  });
}

export function useSessions() {
  const { api } = useBrandex();
  return useQuery<Session[]>({
    queryKey: keys.sessions,
    queryFn: async () => required((await api.http.GET("/v1/auth/sessions")).data),
  });
}

export function useMembers(orgId: string | undefined) {
  const { api } = useBrandex();
  return useQuery<Member[]>({
    queryKey: keys.members(orgId ?? ""),
    enabled: orgId !== undefined,
    queryFn: async () =>
      required(
        (await api.http.GET("/v1/orgs/{org_id}/members", { params: { path: { org_id: orgId ?? "" } } })).data,
      ),
  });
}

export function useRevokeSession() {
  const { api } = useBrandex();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await api.http.DELETE("/v1/auth/sessions/{session_id}", { params: { path: { session_id: sessionId } } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.sessions }),
  });
}

export function useSignOut() {
  const { api, nav } = useBrandex();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (everywhere: boolean) => {
      try {
        await api.http.POST(everywhere ? "/v1/auth/logout-all" : "/v1/auth/logout");
      } finally {
        await api.clearSession();
      }
    },
    onSettled: () => {
      qc.clear();
      nav.navigate("/auth/login");
    },
  });
}

/** The organization's package; `null` when there is none (404). */
export function useMySubscription(orgId: string | undefined) {
  const { api } = useBrandex();
  return useQuery({
    queryKey: keys.subscription(orgId ?? ""),
    enabled: orgId !== undefined,
    queryFn: async () => {
      try {
        return required((await api.http.GET("/v1/orgs/{org_id}/subscription", { params: { path: { org_id: orgId ?? "" } } })).data);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
}

export function useMyChangeRequests(orgId: string | undefined) {
  const { api } = useBrandex();
  return useQuery({
    queryKey: keys.changeRequests(orgId ?? ""),
    enabled: orgId !== undefined,
    queryFn: async () =>
      required((await api.http.GET("/v1/orgs/{org_id}/subscription/change-requests", { params: { path: { org_id: orgId ?? "" } } })).data),
  });
}

/** Plans on offer (public endpoint). */
export function usePublicPlans() {
  const { api } = useBrandex();
  return useQuery({
    queryKey: keys.publicPlans,
    queryFn: async () => required((await api.http.GET("/v1/public/plans")).data),
  });
}

/** The doctor's practice; `null` until onboarding is complete (404). */
export function usePractice(orgId: string | undefined) {
  const { api } = useBrandex();
  return useQuery<Practice | null>({
    queryKey: keys.practice(orgId ?? ""),
    enabled: orgId !== undefined,
    queryFn: async () => {
      try {
        return required((await api.http.GET("/v1/orgs/{org_id}/practice", { params: { path: { org_id: orgId ?? "" } } })).data);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
}

/** Public URL of a doctor website: the custom domain when connected. */
export function websiteUrl(website: { domain?: string | null; subdomain_url?: string | null; path: string }, siteUrl: string): string {
  if (website.domain) return `https://${website.domain}`;
  if (website.subdomain_url) return website.subdomain_url;
  return `${siteUrl.replace(/\/$/, "")}${website.path}`;
}

/** Absolute URL for a media path returned by the API (`/v1/public/media/...`). */
export function mediaUrl(apiBaseUrl: string, path: string): string {
  return `${apiBaseUrl.replace(/\/$/, "")}${path}`;
}

/** The practice's articles (summaries, no bodies), newest first. */
export function useArticles(orgId: string | undefined) {
  const { api } = useBrandex();
  return useQuery<ArticleSummary[]>({
    queryKey: keys.articles(orgId ?? ""),
    enabled: orgId !== undefined,
    queryFn: async () =>
      required((await api.http.GET("/v1/orgs/{org_id}/articles", { params: { path: { org_id: orgId ?? "" } } })).data).items,
  });
}

/** One article with its editor document. */
export function useArticle(orgId: string | undefined, id: string | undefined) {
  const { api } = useBrandex();
  return useQuery<Article>({
    queryKey: keys.article(orgId ?? "", id ?? ""),
    enabled: orgId !== undefined && id !== undefined,
    // The editor owns the text while it is open; refetching must not replace it.
    staleTime: Infinity,
    queryFn: async () =>
      required(
        (await api.http.GET("/v1/orgs/{org_id}/articles/{article_id}", { params: { path: { org_id: orgId ?? "", article_id: id ?? "" } } })).data,
      ),
  });
}

/** Everything that changes an article. Each call keeps the caches current. */
export function useArticleActions(orgId: string) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const remember = (a: Article) => {
    qc.setQueryData(keys.article(orgId, a.id), a);
    void qc.invalidateQueries({ queryKey: keys.articles(orgId), exact: true });
    return a;
  };
  return {
    /** Creates the article when `id` is `null`, else saves it. */
    async save(id: string | null, body: ArticleRequest): Promise<Article> {
      if (id === null) {
        return remember(required((await api.http.POST("/v1/orgs/{org_id}/articles", { params: { path: { org_id: orgId } }, body })).data));
      }
      return remember(
        required((await api.http.PUT("/v1/orgs/{org_id}/articles/{article_id}", { params: { path: { org_id: orgId, article_id: id } }, body })).data),
      );
    },
    async act(id: string, action: ArticleAction): Promise<Article> {
      return remember(
        required(
          (await api.http.POST("/v1/orgs/{org_id}/articles/{article_id}/{action}", { params: { path: { org_id: orgId, article_id: id, action } } })).data,
        ),
      );
    },
    async remove(id: string): Promise<void> {
      await api.http.DELETE("/v1/orgs/{org_id}/articles/{article_id}", { params: { path: { org_id: orgId, article_id: id } } });
      qc.removeQueries({ queryKey: keys.article(orgId, id) });
      await qc.invalidateQueries({ queryKey: keys.articles(orgId), exact: true });
    },
    /** Raw bytes, not JSON: the server checks the real type and re-encodes. */
    async uploadImage(kind: "cover" | "inline", file: File): Promise<ArticleImage> {
      return required(
        (
          await api.http.PUT("/v1/orgs/{org_id}/article-images/{kind}", {
            params: { path: { org_id: orgId, kind } },
            body: file as unknown as number[],
            bodySerializer: (b: unknown) => b as BodyInit,
            headers: { "content-type": file.type || "application/octet-stream" },
          })
        ).data,
      );
    },
  };
}

/**
 * Every social platform's panel (ADR 0024). Polls while a first sync is
 * loading, so new posts appear without a refresh.
 */
export function useSocial(orgId: string | undefined) {
  const { api } = useBrandex();
  return useQuery<SocialPlatform[]>({
    queryKey: keys.social(orgId ?? ""),
    enabled: orgId !== undefined,
    queryFn: async () => required((await api.http.GET("/v1/orgs/{org_id}/social", { params: { path: { org_id: orgId ?? "" } } })).data),
    refetchInterval: (q) => (q.state.data?.some((p) => p.connected && p.sync?.status === "pending") === true ? 10_000 : false),
  });
}

/** A platform's synced posts, a page at a time. */
export function useSocialPosts(orgId: string, platform: SocialPlatform["platform"], filter: SocialPostFilter, refresh: boolean) {
  const { api } = useBrandex();
  const PER_PAGE = 24;
  return useInfiniteQuery<SocialPostPage>({
    queryKey: keys.socialPosts(orgId, platform, filter),
    initialPageParam: 1,
    queryFn: async ({ pageParam }) =>
      required(
        (
          await api.http.GET("/v1/orgs/{org_id}/social/{platform}/posts", {
            params: {
              path: { org_id: orgId, platform },
              query: { sort: filter.sort, show: filter.show, page: Number(pageParam), per_page: PER_PAGE, ...(filter.q.trim() === "" ? {} : { q: filter.q.trim() }) },
            },
          })
        ).data,
      ),
    getNextPageParam: (last) => (last.page * last.per_page < last.total ? last.page + 1 : undefined),
    refetchInterval: refresh ? 10_000 : false,
  });
}
